import {
  InputOverrideParams,
  useGetInitiativeUniversalTrackersQuery,
  useUpdateInitiativeUniversalTrackerMutation,
} from '@api/initiative-universal-trackers';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { isDefined } from '@utils/index';
import { isSimpleNumericColumnType } from '@utils/universalTracker';
import { InputOverrideForm } from './InputOverrideForm';
import { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import {
  getDefaultDecimal,
  getInputType,
  isInputOverridableColumn,
  isSingleTableQuestion,
  getDefaultVariation,
  getDefaultMandatory,
  getInitialUnitConfig,
  getUnitTypeMap,
  hasOverriddenVariations,
} from '../utils';
import { MetricOverrideProps } from '../types';

const hasOverriddenProperty = (
  utr: InitiativeUniversalTracker,
  type: 'decimal' | 'unitInput' | 'numberScaleInput' | 'variations' | 'mandatory',
) => {
  if (type === 'decimal') {
    return (
      isDefined(utr.valueValidation?.[type]) ||
      utr.valueValidation?.table?.columns.some((col) => isDefined(col.validation?.[type]))
    );
  }
  if (type === 'variations') {
    return hasOverriddenVariations(utr);
  }
  if (type === 'mandatory') {
    return utr.valueValidation?.table?.columns?.some((col) => isDefined(col.validation?.required));
  }
  return isDefined(utr[type]) || utr.valueValidation?.table?.columns.some((col) => isDefined(col[type]));
};

export const InputOverrideContainer = (props: MetricOverrideProps) => {
  const { handleReload, selectedQuestions, initiativeId, numericSelectedQuestions, handleCloseModal, formActionsLeftContent } = props;
  const selectedUtrIds = selectedQuestions.map((q) => q._id);

  const { data: initiativeUtrs = [], isFetching: isInitiativeUtrsLoading } = useGetInitiativeUniversalTrackersQuery(
    initiativeId,
    {
      skip: !initiativeId || selectedQuestions.length === 0,
    },
  );

  const [updateInitiativeUtrs, { isLoading: isUpdating }] = useUpdateInitiativeUniversalTrackerMutation();

  const { overriddenQuestions, firstDecimalValidation, firstVariationOverride, firstMandatoryOverride } =
    initiativeUtrs.reduce(
      (acc, utr) => {
        if (!selectedUtrIds.includes(utr.universalTrackerId)) {
          return acc;
        }

        const hasOverriddenDecimal = hasOverriddenProperty(utr, 'decimal');
        const hasOverriddenUnit =
          hasOverriddenProperty(utr, 'unitInput') || hasOverriddenProperty(utr, 'numberScaleInput');
        const hasOverriddenVariation = hasOverriddenProperty(utr, 'variations');
        const hasOverriddenMandatory = hasOverriddenProperty(utr, 'mandatory');

        if (!hasOverriddenDecimal && !hasOverriddenUnit && !hasOverriddenVariation && !hasOverriddenMandatory) {
          return acc;
        }

        if (hasOverriddenDecimal && !acc.firstDecimalValidation) {
          acc.firstDecimalValidation = utr;
        }
        if (hasOverriddenVariation && !acc.firstVariationOverride) {
          acc.firstVariationOverride = utr;
        }
        if (hasOverriddenMandatory && !acc.firstMandatoryOverride) {
          acc.firstMandatoryOverride = utr;
        }
        acc.overriddenQuestions.push(utr);
        return acc;
      },
      {
        overriddenQuestions: [],
        firstDecimalValidation: undefined,
        firstVariationOverride: undefined,
        firstMandatoryOverride: undefined,
      } as {
        overriddenQuestions: InitiativeUniversalTracker[];
        firstDecimalValidation: InitiativeUniversalTracker | undefined;
        firstVariationOverride: InitiativeUniversalTracker | undefined;
        firstMandatoryOverride: InitiativeUniversalTracker | undefined;
      },
    );

  const overridableColumns = isSingleTableQuestion(selectedQuestions)
    ? selectedQuestions[0].valueValidation?.table?.columns?.filter((c) => isInputOverridableColumn(c)) || []
    : [];

  const numericColumns = overridableColumns.filter((c) => isSimpleNumericColumnType(c));

  const inputType = getInputType(selectedQuestions);

  const defaultDecimal = getDefaultDecimal({
    inputType,
    tableColumns: numericColumns,
    valueValidation: firstDecimalValidation?.valueValidation,
  });

  const unitTypeMap = getUnitTypeMap({ selectedQuestions, overriddenQuestions, inputType });
  const defaultUnitConfig = getInitialUnitConfig({ inputType, tableColumns: numericColumns, unitTypeMap });

  const defaultVariation = getDefaultVariation({
    inputType,
    tableColumns: numericColumns,
    valueValidation: firstVariationOverride?.valueValidation,
  });

  const defaultMandatory = getDefaultMandatory({
    inputType,
    tableColumns: overridableColumns,
    utrValueValidation: selectedQuestions[0]?.valueValidation,
    initiativeValueValidation: firstMandatoryOverride?.valueValidation,
    isCustomMetric: Boolean(selectedQuestions[0]?.ownerId),
  });

  const handleUpdate = ({
    decimal,
    unitConfig,
    variation,
    mandatory,
  }: Pick<InputOverrideParams, 'decimal' | 'unitConfig' | 'variation' | 'mandatory'>) => {
    // Use selectedQuestions for table with overridable columns (mandatory), 
    // otherwise use numericSelectedQuestions
    const questionsToUpdate = overridableColumns.length > 0 ? selectedQuestions : numericSelectedQuestions;
    const data = {
      decimal,
      unitConfig,
      variation,
      mandatory,
      initiativeId,
      utrIds: questionsToUpdate.map((q) => q._id),
    };
    updateInitiativeUtrs(data)
      .unwrap()
      .then(() => {
        generateToast({ color: 'success', message: 'Input overrides updated successfully' });
        handleReload({ reloadSurvey: true, closeModal: selectedQuestions.length === 1 });
      })
      .catch((error) => {
        toastFromError({ error, title: 'Failed to update input overrides' });
        handleReload();
      });
  };

  return (
    <InputOverrideForm
      key={`${JSON.stringify(defaultDecimal)}-${JSON.stringify(defaultUnitConfig)}-${JSON.stringify(defaultVariation)}-${JSON.stringify(defaultMandatory)}`}
      isLoading={isInitiativeUtrsLoading || isUpdating}
      selectedQuestions={selectedQuestions}
      overridableColumns={overridableColumns}
      handleUpdate={handleUpdate}
      overriddenQuestions={overriddenQuestions}
      defaultDecimal={defaultDecimal}
      defaultUnitConfig={defaultUnitConfig}
      defaultVariation={defaultVariation}
      defaultMandatory={defaultMandatory}
      unitTypeMap={unitTypeMap}
      handleCloseModal={handleCloseModal}
      formActionsLeftContent={formActionsLeftContent}
    />
  );
};
